import { useHandler } from "@redotech/react-util/hook";
import { ANONYMOUS_SHOPPER_ID_KEY } from "@redotech/redo-web/utils/shared-conf";
import { getShopifySessionId } from "@redotech/redo-web/utils/shopify-session";
import { getSessionHazardProbabilities } from "@redotech/shopify-sdk/rpc/schema/checkout-optimization/get-session-hazard-probabilities";
import {
  Context,
  createContext,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { RedoShopifyConversionRpcClientContext } from "../../client/redo-shopify-rpc-client";
import { log } from "../../monitoring/log";
import {
  loadCheckoutProbabilityFromSessionStorage,
  saveCheckoutProbabilityToSessionStorage,
} from "../../utils/checkout-probability";

const UPDATE_INTERVAL_MS = 15_000;
const STALE_PAGE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

interface CheckoutProbabilityContextValue {
  cumulativeConversionProbability: number | null;
  instantaneousHazardProbability: number | null;
  previousCumulativeConversionProbability: number | null;
  previousInstantaneousHazardProbability: number | null;
  sessionAgeInMs: number | null;
}

export const CheckoutProbabilityContext: Context<
  CheckoutProbabilityContextValue | undefined
> = createContext<CheckoutProbabilityContextValue | undefined>(undefined);
CheckoutProbabilityContext.displayName = "CheckoutProbabilityContext";

const getInitialState = (): CheckoutProbabilityContextValue => {
  const stored = loadCheckoutProbabilityFromSessionStorage();
  return {
    cumulativeConversionProbability: null,
    instantaneousHazardProbability: null,
    // Use stored values as previous values if they exist
    previousCumulativeConversionProbability:
      stored?.cumulativeConversionProbability || null,
    previousInstantaneousHazardProbability:
      stored?.instantaneousHazardProbability || null,
    sessionAgeInMs: null,
  };
};

export const CheckoutProbabilityContextProvider = memo(
  function CheckoutProbabilityContextProvider({
    children,
    hasActiveModel,
  }: {
    children: ReactNode | ReactNode[];
    hasActiveModel: boolean;
  }) {
    const shopifyRpcClient = useContext(RedoShopifyConversionRpcClientContext);

    const [state, setState] =
      useState<CheckoutProbabilityContextValue>(getInitialState);

    const intervalRef = useRef<NodeJS.Timeout>();
    const pageLoadTimeRef = useRef<number>(
      Temporal.Now.instant().epochMilliseconds,
    );

    const updateCheckoutProbability = useHandler(async () => {
      try {
        if (!hasActiveModel) {
          // No trained hazard model — leave probabilities null. The rescue
          // panel falls back to its deterministic stale-page trigger.
          return;
        }

        // Check if page has been stale for more than 10 minutes
        const timeSincePageLoad =
          Temporal.Now.instant().epochMilliseconds - pageLoadTimeRef.current;
        if (timeSincePageLoad > STALE_PAGE_TIMEOUT_MS) {
          log(
            `Page has been stale for ${Math.round(timeSincePageLoad / 1000 / 60)} minutes, skipping checkout probability update`,
          );
          return;
        }

        const shopifySessionId = getShopifySessionId();
        if (!shopifySessionId) {
          log(
            "Shopify session ID not found, skipping checkout probability update",
          );
          return;
        }

        const anonymousShopperId = localStorage.getItem(
          ANONYMOUS_SHOPPER_ID_KEY,
        );
        if (!anonymousShopperId) {
          log(
            "Anonymous shopper ID not found, skipping checkout probability update",
          );
          return;
        }

        if (!shopifyRpcClient) {
          console.warn("Shopify RPC client not available");
          return;
        }

        const storeUrl =
          globalThis.redoStorefront?.storeUrl ?? globalThis.Shopify?.shop;
        if (!storeUrl) {
          console.warn("Store URL not available");
          return;
        }

        // Get the current checkout probability from RPC endpoint
        const result = await shopifyRpcClient(getSessionHazardProbabilities, {
          sessionId: shopifySessionId,
          anonymousShopperId,
        });

        if (!result) {
          console.warn("No result returned from RPC");
          return;
        }

        log(
          "result.instantaneousHazardProbability",
          result.instantaneousHazardProbability,
        );

        // Update all values in one setState call
        setState((prev) => {
          const newState: CheckoutProbabilityContextValue = {
            ...prev,
            previousCumulativeConversionProbability:
              prev.cumulativeConversionProbability,
            previousInstantaneousHazardProbability:
              prev.instantaneousHazardProbability,
            cumulativeConversionProbability:
              result.cumulativeConversionProbability,
            instantaneousHazardProbability:
              result.instantaneousHazardProbability,
            sessionAgeInMs: result.sessionAge,
          };

          // Save current values to SessionStorage for next page load
          saveCheckoutProbabilityToSessionStorage({
            cumulativeConversionProbability:
              newState.cumulativeConversionProbability,
            instantaneousHazardProbability:
              newState.instantaneousHazardProbability,
          });

          return newState;
        });
      } catch (err) {
        console.error("Error in checkout probability context:", err);
      }
    });

    useEffect(() => {
      if (!hasActiveModel) {
        return;
      }

      // Initial update
      void updateCheckoutProbability();

      // Set up interval to update every 15 seconds
      intervalRef.current = setInterval(
        updateCheckoutProbability,
        UPDATE_INTERVAL_MS,
      );

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }, [hasActiveModel, updateCheckoutProbability]);

    return (
      <CheckoutProbabilityContext.Provider value={state}>
        {children}
      </CheckoutProbabilityContext.Provider>
    );
  },
);
